import { Money } from "@redotech/money";
import { RateSource } from "@redotech/money/rate-source";
import { Service } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import {
  RedoShopifyDeliveryMethodType,
  RedoShopifyDeliveryMethodTypeToString,
} from "@redotech/redo-fulfillment-group-common/fulfillment-delivery-method-type";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  FulfillmentGroupStatus,
  isDoneStatus,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { ShipmentFlag } from "@redotech/redo-fulfillment-operations-common/shipment-flag";
import LocalPickupLogo from "@redotech/redo-icons/building-01.svg";
import TruckIcon from "@redotech/redo-icons/truck-02.svg";
import { getCarrierServiceLabel } from "@redotech/redo-web/carrier-service-labels";
import { getCarrierLogoUrl } from "@redotech/redo-web/carrier/logo";
import { Flex } from "@redotech/redo-web/flex";
import { MoneyText } from "@redotech/redo-web/fx/money-text";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import { memo, useMemo } from "react";
import { ShipmentFlagsDisplay } from "../../common/shipment-flags-display";
import * as styles from "./shipping-selection-cell.module.css";

export interface ShippingSelectionCellData {
  carrierName: Carriers | null;
  serviceName: Service | null;
  /** Rate as exact Money in its rated currency; MoneyText converts for display. */
  rateMoney: Money | null;
  deliveryDays: number | null;
}

export const ShippingSelectionCell = memo(function ShippingSelectionCell({
  data,
  hideDeliveryDays,
  flags = [],
  deliveryMethod,
  status,
  hideLogo,
}: {
  data: ShippingSelectionCellData | null;
  hideDeliveryDays?: boolean;
  flags?: readonly ShipmentFlag[];
  deliveryMethod?: RedoShopifyDeliveryMethodType | null;
  status: FulfillmentGroupStatus;
  hideLogo?: boolean;
}) {
  const logoUrl = useMemo(() => {
    return data?.carrierName ? getCarrierLogoUrl(data.carrierName) : null;
  }, [data]);

  const deliveryDays = useMemo(() => {
    if (!data?.deliveryDays) {
      return "";
    }
    return ` - ${data.deliveryDays} days`;
  }, [data]);

  if (deliveryMethod === RedoShopifyDeliveryMethodType.PickUp) {
    return (
      <Flex align="center" dir="row" gap="lg" p="xs">
        {!hideLogo && <LocalPickupLogo color="ghost" height={14} width={14} />}
        <Text fontSize="xs">
          {
            RedoShopifyDeliveryMethodTypeToString[
              deliveryMethod as RedoShopifyDeliveryMethodType
            ]
          }
        </Text>
      </Flex>
    );
  }

  if (deliveryMethod === RedoShopifyDeliveryMethodType.Local) {
    return (
      <Flex align="center" dir="row" gap="lg" p="xs">
        {!hideLogo && <TruckIcon color="ghost" height={14} width={14} />}
        <Text fontSize="xs">
          {
            RedoShopifyDeliveryMethodTypeToString[
              deliveryMethod as RedoShopifyDeliveryMethodType
            ]
          }
        </Text>
      </Flex>
    );
  }

  if (deliveryMethod === RedoShopifyDeliveryMethodType.Freight) {
    return (
      <Flex align="center" dir="row" gap="lg" p="xs">
        <TruckIcon color="ghost" height={14} width={14} />
        <Text fontSize="xs">
          {
            RedoShopifyDeliveryMethodTypeToString[
              deliveryMethod as RedoShopifyDeliveryMethodType
            ]
          }
        </Text>
      </Flex>
    );
  }

  return (
    <Flex align="center">
      {data?.carrierName ? (
        <Text fontSize="xs">
          {!hideLogo && logoUrl && (
            <img className={classNames(styles.nameLogo)} src={logoUrl} />
          )}
          {getCarrierServiceLabel(data.carrierName, data.serviceName)}
          {!hideDeliveryDays && data.rateMoney && (
            <Text as="span" fontWeight="thin" textColor="tertiary">
              {" - "}
              <MoneyText source={RateSource.Stripe} value={data.rateMoney} />
              {deliveryDays}
            </Text>
          )}
        </Text>
      ) : flags.length > 0 ? (
        <ShipmentFlagsDisplay flags={flags} />
      ) : (
        <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
          {isDoneStatus(status) ? "" : "No rate selected"}
        </Text>
      )}
    </Flex>
  );
});

// Determines what data to show in the shipping selection cell
// This can come from data manually entered by the user, the rate that was purchased, or their selected rate, if unfulfilled
export function getShippingSelectionCellData(
  row: FulfillmentGroupData,
): ShippingSelectionCellData | null {
  if (!row.fulfillmentInfo) {
    return null;
  }

  return {
    carrierName: row.fulfillmentInfo?.carrier ?? null,
    serviceName: row.fulfillmentInfo?.service ?? null,
    rateMoney: row.fulfillmentInfo?.rateMoney ?? null,
    deliveryDays: row.fulfillmentInfo?.estimatedDeliveryDays ?? null,
  };
}
